<template>
  <div class="on-site-receipt">
    <button
      v-if="items.length"
      type="button"
      class="on-site-receipt__toggle"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span>
        {{
          open
            ? $t('mollie.work_order_complete.hide_lines')
            : $t('mollie.work_order_complete.show_lines', { count: items.length })
        }}
      </span>
      <BaseIcon
        name="chevron"
        size="14"
        :rotate="open ? 0 : 180"
        class="on-site-receipt__toggle-icon"
      />
    </button>

    <ul
      v-if="items.length"
      class="on-site-receipt__lines"
      :class="{ 'on-site-receipt__lines--collapsed': !open }"
    >
      <li
        v-for="(item, index) in items"
        :key="item.id || index"
        class="on-site-receipt__line"
        :class="{
          'on-site-receipt__line--deduction': item.item_type === 'deposit_deduction',
          'on-site-receipt__line--discount': item.item_type === 'discount',
        }"
      >
        <div v-if="hasAnyImage" class="on-site-receipt__thumb">
          <img
            v-if="item.image_url"
            :src="item.image_url"
            :alt="stripChildPrefix(item.description)"
            loading="lazy"
          />
          <BaseIcon v-else name="package" size="16" />
        </div>
        <div class="on-site-receipt__line-body">
          <div class="on-site-receipt__line-description">
            {{ stripChildPrefix(item.description) }}
          </div>
          <div class="on-site-receipt__line-meta">
            {{ formatQty(item.quantity) }} × {{ formatCurrency(unitPrice(item), currency) }}
          </div>
        </div>
        <div class="on-site-receipt__line-total">
          {{ formatCurrency(lineTotal(item), currency) }}
        </div>
      </li>
    </ul>

    <div class="on-site-receipt__totals">
      <div v-if="invoice.subtotal_excl_vat != null" class="on-site-receipt__totals-row">
        <span>{{ $t('invoices.editor.subtotal_excl_vat') }}</span>
        <span>{{ formatCurrency(invoice.subtotal_excl_vat, currency) }}</span>
      </div>
      <template v-if="vatRows.length">
        <div v-for="row in vatRows" :key="row.rate" class="on-site-receipt__totals-row">
          <span>{{ $t('invoices.detail.vat') }} ({{ row.rate }}%)</span>
          <span>{{ formatCurrency(row.vat, currency) }}</span>
        </div>
      </template>
      <div v-if="depositDeduction > 0" class="on-site-receipt__totals-row">
        <span>{{ $t('invoices.detail.deposit_deduction') }}</span>
        <span>-{{ formatCurrency(depositDeduction, currency) }}</span>
      </div>
      <div class="on-site-receipt__totals-row on-site-receipt__totals-row--due">
        <span>{{ $t('mollie.work_order_complete.amount_due') }}</span>
        <span>{{ formatCurrency(amountDue, currency) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, ref, getCurrentInstance } from 'vue';

  import BaseIcon from '@/components/partials/BaseIcon.vue';
  import { formatCurrency } from '@/utils/currency';
  import { displayUnitPrice, resolveDisplayBasis } from '@shared/utils/moneyUtils';
  import { stripChildPrefix } from '@shared/utils/proposalLineItems';
  import { resolveAmountDue } from '@/utils/invoiceAmounts';

  export default {
    name: 'OnSiteReceipt',

    components: { BaseIcon },

    props: {
      invoice: { type: Object, required: true },
      defaultOpen: { type: Boolean, default: false },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const open = ref(props.defaultOpen);

      const items = computed(() => {
        const list = Array.isArray(props.invoice?.items) ? props.invoice.items : [];
        return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
      });

      const currency = computed(() => props.invoice?.currency || 'EUR');
      const displayBasis = computed(() => resolveDisplayBasis(props.invoice || {}));

      const hasAnyImage = computed(() => items.value.some((item) => !!item.image_url));

      const unitPrice = (item) => displayUnitPrice(item, displayBasis.value);
      const lineTotal = (item) => (Number(item.quantity) || 0) * unitPrice(item);

      const formatQty = (qty) => {
        const n = Number(qty);
        if (!Number.isFinite(n)) return '0';
        return Number.isInteger(n) ? n.toString() : n.toFixed(2);
      };

      const vatRows = computed(() => {
        const breakdown = props.invoice?.vat_breakdown;
        if (Array.isArray(breakdown) && breakdown.length) {
          return breakdown.map((row) => ({ rate: Number(row.rate), vat: Number(row.vat) }));
        }
        if (props.invoice?.vat_amount != null) {
          return [
            {
              rate: props.invoice.vat_rate != null ? Number(props.invoice.vat_rate) : '',
              vat: Number(props.invoice.vat_amount),
            },
          ];
        }
        return [];
      });

      const depositDeduction = computed(() => Number(props.invoice?.deposit_deduction_amount) || 0);
      const amountDue = computed(() => resolveAmountDue(props.invoice));

      return {
        $t,
        open,
        items,
        currency,
        hasAnyImage,
        unitPrice,
        lineTotal,
        formatQty,
        vatRows,
        depositDeduction,
        amountDue,
        formatCurrency,
        stripChildPrefix,
      };
    },
  };
</script>

<style scoped lang="scss">
  .on-site-receipt {
    display: flex;
    flex-direction: column;
    text-align: left;
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;
    background: white;
    overflow: hidden;

    &__toggle {
      display: none;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 0.75rem 1rem;
      border: 0;
      border-bottom: 1px solid var(--slate-100);
      background: var(--slate-50);
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);
      cursor: pointer;

      @include lg-down {
        display: flex;
      }
    }

    &__toggle-icon {
      color: var(--slate-500);
    }

    &__lines {
      list-style: none;
      margin: 0;
      padding: 0;

      @include lg-down {
        &--collapsed {
          display: none;
        }
      }
    }

    &__line {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--slate-100);

      &--deduction {
        background: var(--blue-50);

        .on-site-receipt__line-description,
        .on-site-receipt__line-total {
          color: var(--blue-700);
        }
      }

      &--discount {
        .on-site-receipt__line-description,
        .on-site-receipt__line-total {
          color: var(--green-700);
        }
      }
    }

    &__thumb {
      flex: 0 0 auto;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.5rem;
      background: var(--slate-100);
      color: var(--slate-400);
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    &__line-body {
      flex: 1;
      min-width: 0;
    }

    &__line-description {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-900);
      line-height: 1.35;
      @include ellipsis(2);
    }

    &__line-meta {
      margin-top: 0.125rem;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__line-total {
      flex: 0 0 auto;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      white-space: nowrap;
    }

    &__totals {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 0.75rem 1rem;
      background: var(--slate-50);
    }

    &__totals-row {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      font-size: 0.8125rem;
      color: var(--slate-600);

      span:last-child {
        font-weight: 500;
        color: var(--slate-800);
        white-space: nowrap;
      }

      &--due {
        margin-top: 0.375rem;
        padding-top: 0.625rem;
        border-top: 1px solid var(--slate-200);
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--slate-900);

        span:last-child {
          font-size: 1.0625rem;
          font-weight: 700;
          color: var(--slate-900);
        }
      }
    }
  }
</style>
